import Posts, { IPosts } from '@/models/Posts'

import { handlePostEdited } from '@/helper/posts.helper'
import Channel from '@/models/Channels'
import { PostsService } from '@/service'
import { convertToRelativeTime } from '@/util/dateTimeConversion'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import { reactive } from 'vue'
import { GroupState } from './groups'

export class PostsState {
  state = reactive({
    fetching: false,
    error: null as any,
    pinnedPosts: new Map<string, IPosts[]>(),
    isNextPinnedPostsPageAvaialble: new Map<string, boolean>(),
    isPinnedPostsPageLoading: new Map<string, boolean>(),

    posts: [] as IPosts[],
    postPages: new Set(),
    nextPage: true,
  })
  async fetchPost(postId: string): Promise<IPosts | undefined> {
    try {
      const foundPost = PostState.state.posts.find(
        (post: any) => post._id === postId
      )
      if (foundPost) {
        return foundPost
      }
      const post = await PostsService.getPost(postId)
      if (typeof post === 'string') {
        return undefined
      }
      post.relativeTime = convertToRelativeTime(post.createdAt)
      return post
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }
  async fetchPosts(route: any, query?: any): Promise<void> {
    try {
      let newPosts = []

      //Reset post if when fetching first page
      if (!query.previousId) {
        this.state.posts = []
        this.state.postPages = new Set()
        this.state.nextPage = true
      }

      //Page already fetched
      if (query.previousId && this.state.postPages.has(query.previousId)) {
        return
      }

      //nextPage Not available
      if (!this.state.nextPage) {
        return
      }

      this.state.fetching = true
      if (route.params?.channelSlug) {
        const channel = new Channel(
          GroupState.state.channelList.find(
            (channel: any) => channel.slug === route.params.channelSlug
          )
        )
        newPosts = await PostsService.getChannelPosts(channel.id, query)
        if (query.previousId) {
          this.state.posts.push(...newPosts)
        } else {
          this.state.posts = newPosts
        }
      } else if (route.params.userSlug) {
        try {
          newPosts = await PostsService.getPostsByUser({
            slug: route.params.userSlug,
            ...query,
          })
          if (query.previousId) {
            this.state.posts.push(...newPosts)
          } else {
            this.state.posts = newPosts
          }
        } catch (error: any) {
          // in case of user is not found in this case do not throw error ignore the error and do not update state.posts
          if (error?.response?.data?.message != 'User not found') {
            throw error
          }
        }
      } else if (route.params.groupSlug) {
        newPosts = await PostsService.getPublicPosts(query)
        this.state.posts.push(...newPosts)
      } else {
        newPosts = await PostsService.getPublicPosts(query)
        this.state.posts.push(...newPosts)
      }

      //To avoid repeative fetching of same page
      if (query.previousId) {
        this.state.postPages.add(query.previousId)
      }

      //Check if nextPage is available
      if (query.limit && newPosts.length < query.limit) {
        this.state.nextPage = false
      }

      this.state.posts = this.state.posts.map(post => {
        post.relativeTime = convertToRelativeTime(post.createdAt)
        return post
      })
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(() => (this.state.fetching = false), 1000)
    }
  }

  async fetchPinnedPostsForChannel(
    channelSlug: string,
    paginationParams?: any
  ): Promise<void> {
    try {
      let existingPinnedPosts: IPosts[] | undefined = undefined

      if (
        this.state.isNextPinnedPostsPageAvaialble.get(channelSlug) === false
      ) {
        return
      }
      existingPinnedPosts = this.state.pinnedPosts.get(channelSlug)

      //Page already fetched
      if (existingPinnedPosts && paginationParams.previousPaginationKeyValue) {
        const prevPostIndex = existingPinnedPosts?.findIndex(
          post =>
            post.channelPinnedIndex ===
            paginationParams.previousPaginationKeyValue
        )
        // check if the previous post is the last post in the pinned posts list, if its not do nothing
        if (prevPostIndex != existingPinnedPosts.length - 1) {
          return
        }
      } else if (
        existingPinnedPosts &&
        !paginationParams.previousPaginationKeyValue
      ) {
        //since paginationKeyValue is not present, and first page is already fetched, do nothing
        return
      }

      // if the pinned posts page is already loading, do nothing
      if (this.state.isPinnedPostsPageLoading.get(channelSlug)) {
        return
      }

      this.state.isPinnedPostsPageLoading.set(channelSlug, true)
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const newPinnedPosts = await PostsService.getChannelPinnedPosts(
        channel.id,
        paginationParams
      )
      if (!existingPinnedPosts || existingPinnedPosts.length < 1) {
        this.state.pinnedPosts.set(channelSlug, [] as IPosts[])
        existingPinnedPosts = this.state.pinnedPosts.get(channelSlug)
      }
      // ensure that duplicate posts are not added to pinnedPost array
      newPinnedPosts.forEach((post: any) => {
        const postIndex = existingPinnedPosts?.findIndex(
          postInArray => postInArray._id === post._id
        )
        if (!postIndex || postIndex === -1) {
          post.relativeTime = convertToRelativeTime(post.createdAt)
          existingPinnedPosts?.push(post)
        }
      })
      this.state.isPinnedPostsPageLoading.set(channelSlug, false)
      //set next page available to false if the number of posts fetched is less than the limit
      if (
        paginationParams.limit &&
        newPinnedPosts.length < paginationParams.limit
      ) {
        this.state.isNextPinnedPostsPageAvaialble.set(channelSlug, false)
      } else {
        this.state.isNextPinnedPostsPageAvaialble.set(channelSlug, true)
      }
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(
        () => this.state.isPinnedPostsPageLoading.set(channelSlug, false),
        1000
      )
    }
  }

  async fetchPinnedPostsForHome(paginationParams?: any): Promise<void> {
    const groupSlug = GroupState.state.groupData.slug

    try {
      let existingPinnedPosts: IPosts[] | undefined = undefined

      if (this.state.isNextPinnedPostsPageAvaialble.get(groupSlug) === false) {
        return
      }
      existingPinnedPosts = this.state.pinnedPosts.get(groupSlug)

      //Page already fetched
      if (existingPinnedPosts && paginationParams.previousPaginationKeyValue) {
        const prevPostIndex = existingPinnedPosts?.findIndex(
          post =>
            post.homePinnedIndex === paginationParams.previousPaginationKeyValue
        )
        // check if the previous post is the last post in the pinned posts list, if its not do nothing
        if (prevPostIndex != existingPinnedPosts.length - 1) {
          return
        }
      } else if (
        existingPinnedPosts &&
        !paginationParams.previousPaginationKeyValue
      ) {
        //since paginationKeyValue is not present, and first page is already fetched, do nothing
        return
      }

      // if the pinned posts page is already loading, do nothing
      if (this.state.isPinnedPostsPageLoading.get(groupSlug)) {
        return
      }

      this.state.isPinnedPostsPageLoading.set(groupSlug, true)

      const newPinnedPosts = await PostsService.getHomePinnedPosts(
        paginationParams
      )
      if (!existingPinnedPosts || existingPinnedPosts.length < 1) {
        this.state.pinnedPosts.set(groupSlug, [] as IPosts[])
        existingPinnedPosts = this.state.pinnedPosts.get(groupSlug)
      }
      // ensure that duplicate posts are not added to pinnedPost array
      newPinnedPosts.forEach((post: any) => {
        const postIndex = existingPinnedPosts?.findIndex(
          postInArray => postInArray._id === post._id
        )
        if (!postIndex || postIndex === -1) {
          post.relativeTime = convertToRelativeTime(post.createdAt)
          existingPinnedPosts?.push(post)
        }
      })
      this.state.isPinnedPostsPageLoading.set(groupSlug, false)
      //set next page available to false if the number of posts fetched is less than the limit
      if (
        paginationParams.limit &&
        newPinnedPosts.length < paginationParams.limit
      ) {
        this.state.isNextPinnedPostsPageAvaialble.set(groupSlug, false)
      } else {
        this.state.isNextPinnedPostsPageAvaialble.set(groupSlug, true)
      }
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(
        () => this.state.isPinnedPostsPageLoading.set(groupSlug, false),
        1000
      )
    }
  }

  async createPost(postData: IPosts): Promise<IPosts> {
    try {
      const post = new Posts(postData)

      await post.save()
      post.data.current_user_reactions = {
        LIKE: false,
      }
      post.data.relativeTime = convertToRelativeTime(post.data.createdAt)

      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel._id === post.channelId
        )
      )
      post.data.channelSlug = channel.slug
      this.state.posts.unshift(post.data)
      GroupState.state.groupData.postsCount += 1
      trackCustomGAEvents(GAEventTypes.POST_CREATED, {
        createdBy: postData.createdBy,
        locationId: GroupState.state.groupData.locationId,
      })
      return post.data
    } catch (error: any) {
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }

  async updatePost(postData: IPosts): Promise<any> {
    try {
      return await PostsService.updatePost(
        postData,
        postData.channelId,
        postData._id as string
      )
    } catch (error: any) {
      console.error(error)
      throw error
    }
  }

  async pinPostToChannelTimeline(
    postId: string,
    channelSlug: string
  ): Promise<void> {
    try {
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const pinnedPost = await PostsService.pinPostToChannel(postId, channel.id)
      const post = this.state.posts.find(post => post._id === postId)
      post!.channelPinnedIndex = pinnedPost.channelPinnedIndex

      // we need to deep clone post object so that the changes in the post object does not reflect in the pinned post object
      const clonedPost = JSON.parse(JSON.stringify(post))
      this.state.pinnedPosts.get(channelSlug)?.unshift(clonedPost!)
    } catch (error: any) {
      console.error(error)
    }
  }

  async pinPostToHomeTimeline(
    postId: string,
    channelId: string
  ): Promise<void> {
    try {
      const pinnedPost = await PostsService.pinPostToHome(postId, channelId)
      const post = this.state.posts.find(post => post._id === postId)
      post!.homePinnedIndex = pinnedPost.homePinnedIndex

      // we need to deep clone post object so that the changes in the post object does not reflect in the pinned post object
      const clonedPost = JSON.parse(JSON.stringify(post))
      this.state.pinnedPosts
        .get(GroupState.state.groupData.slug)
        ?.unshift(clonedPost)
    } catch (error: any) {
      console.error(error)
    }
  }

  async unpinPostFromChannelTimeline(
    postId: string,
    channelSlug: string
  ): Promise<void> {
    try {
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const pinnedPost = await PostsService.unpinPostFromChannel(
        postId,
        channel.id
      )
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.channelPinnedIndex = pinnedPost.channelPinnedIndex
      }
      const pinnedPostIndex = this.state.pinnedPosts
        .get(channelSlug)!
        .findIndex(post => post._id === postId)
      this.state.pinnedPosts.get(channelSlug)?.splice(pinnedPostIndex, 1)
    } catch (error: any) {
      console.error(error)
    }
  }

  async unpinPostFromHomeTimeline(
    postId: string,
    channelId: string
  ): Promise<void> {
    try {
      const pinnedPost = await PostsService.unpinPostFromHome(postId, channelId)
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.homePinnedIndex = pinnedPost.homePinnedIndex
      }
      const pinnedPostIndex = this.state.pinnedPosts
        .get(GroupState.state.groupData.slug)!
        .findIndex(post => post._id === postId)

      this.state.pinnedPosts
        .get(GroupState.state.groupData.slug)
        ?.splice(pinnedPostIndex, 1)
    } catch (error: any) {
      console.error(error)
    }
  }

  async movePostToChannel(
    postId: string,
    channelId: string,
    route: any
  ): Promise<any> {
    try {
      await PostsService.updatePostChannel(postId, channelId)
      const post = await PostsService.getPost(postId)
      post.relativeTime = convertToRelativeTime(post.createdAt)
      // if channel view is open then remove post from the timeline
      if (route?.params?.channelSlug) {
        console.log('removing post from channel')
        this.state.posts = this.state.posts.filter(p => p._id !== postId)
      }
      handlePostEdited(post)
      return post
    } catch (error: any) {
      console.error(error)
    }
  }

  async deletePost(postId: string): Promise<void> {
    const postIndex = this.state.posts.findIndex(post => postId === post._id)
    if (postIndex !== -1) {
      this.state.posts.splice(postIndex, 1)
    }
    GroupState.state.groupData.postsCount -= 1
    PostsService.deletePost(postId)
  }
}

export const PostState = new PostsState()
